import { apiRequest } from '../lib/api-client';

/** One record, its timeline and its tags (context.md sections 16, 106-115). */

export type CrmEntity = 'leads' | 'contacts' | 'companies' | 'opportunities';
export type CrmEntityType = 'LEAD' | 'CONTACT' | 'COMPANY' | 'OPPORTUNITY';

/** The route takes the singular upper-case form; the listings use the plural lower-case one. */
export const ENTITY_TYPE: Record<CrmEntity, CrmEntityType> = {
  leads: 'LEAD',
  contacts: 'CONTACT',
  companies: 'COMPANY',
  opportunities: 'OPPORTUNITY',
};

export interface Activity {
  id: string;
  entity_type: CrmEntityType;
  entity_id: string;
  activity_type: string;
  subject: string | null;
  body: string | null;
  data: Record<string, unknown>;
  occurred_at: string;
  duration_minutes: number | null;
  actor_type: string;
  actor_id: string | null;
}

/** Records differ enough that the detail screen reads them as a bag of fields. */
export type RecordDetail = Record<string, unknown> & { id: string };

export function getRecord(entity: CrmEntity, id: string): Promise<RecordDetail> {
  return apiRequest(`/v1/crm/${entity}/${id}`);
}

export function getTimeline(
  entityType: CrmEntityType,
  id: string,
): Promise<{ activities: Activity[]; next_cursor: string | null }> {
  return apiRequest(`/v1/crm/${entityType}/${id}/timeline?limit=100`);
}

/** Saving a change from the record screen: the same PATCH the list uses, one field at a time. */
export function updateRecord(
  entity: CrmEntity,
  id: string,
  changes: Record<string, unknown>,
): Promise<RecordDetail> {
  return apiRequest(`/v1/crm/${entity}/${id}`, { method: 'PATCH', body: changes });
}

export const LIFECYCLE_STAGES = [
  'LEAD',
  'MQL',
  'SQL',
  'OPPORTUNITY',
  'CUSTOMER',
  'EVANGELIST',
  'OTHER',
] as const;

export type LifecycleStage = (typeof LIFECYCLE_STAGES)[number];

export const BANT_CRITERIA = ['budget', 'authority', 'need', 'timing'] as const;
export const BANT_ANSWERS = ['UNKNOWN', 'NO', 'PARTIAL', 'YES'] as const;

export type BantCriterion = (typeof BANT_CRITERIA)[number];
export type BantAnswer = (typeof BANT_ANSWERS)[number];

export interface Qualification {
  score: number;
  answered: number;
  blockers: BantCriterion[];
  qualified: boolean;
}

export function logActivity(
  entityType: CrmEntityType,
  id: string,
  input: { activityType: string; subject?: string; body: string },
): Promise<Activity> {
  return apiRequest(`/v1/crm/${entityType}/${id}/activities`, { method: 'POST', body: input });
}

export interface Customer360 {
  company: RecordDetail;
  contacts: RecordDetail[];
  opportunities: RecordDetail[];
  totals: Record<string, number>;
}

export function getCustomer360(companyId: string): Promise<Customer360> {
  return apiRequest(`/v1/crm/companies/${companyId}/360`);
}

// ---------------------------------------------------------------------------
// The deal screen (context.md sections 35, 131)
// ---------------------------------------------------------------------------

export interface DealItem {
  id: string;
  product_id: string | null;
  description: string;
  quantity: number;
  unit_price_cents: number;
  discount_bps: number;
  total_cents: number;
}

export function listDealItems(id: string): Promise<{ items: DealItem[]; total_cents: number }> {
  return apiRequest(`/v1/crm/opportunities/${id}/items`);
}

export function saveDealItems(
  id: string,
  items: {
    productId?: string | null;
    description?: string;
    quantity?: number;
    unitPriceCents?: number;
    discountBps?: number;
  }[],
): Promise<{ items: DealItem[]; total_cents: number }> {
  return apiRequest(`/v1/crm/opportunities/${id}/items`, { method: 'PUT', body: { items } });
}

export interface Attribution {
  source?: string | null;
  landing_page?: string | null;
  referrer?: string | null;
  campaign?: string | null;
  medium?: string | null;
  content?: string | null;
  term?: string | null;
  /** The ad campaign the lead was attributed to (Phase 29). */
  ad: string | null;
}

export function dealAttribution(id: string): Promise<Attribution> {
  return apiRequest(`/v1/crm/opportunities/${id}/attribution`);
}

export interface ProductView {
  id: string;
  name: string;
  sku: string | null;
  description: string | null;
  unit_price_cents: number;
  unit: string;
  is_recurring: boolean;
  is_active: boolean;
}

export function listProducts(): Promise<{ products: ProductView[] }> {
  return apiRequest('/v1/crm/products');
}

export function createProduct(input: {
  name: string;
  unitPriceCents?: number;
  unit?: string;
  sku?: string | null;
}): Promise<ProductView> {
  return apiRequest('/v1/crm/products', { method: 'POST', body: input });
}

export interface DealView {
  id: string;
  title: string;
  company_id: string | null;
  contact_id: string | null;
  pipeline_id: string;
  stage_id: string;
  amount_cents: number;
  status: 'OPEN' | 'WON' | 'LOST';
  expected_close_date: string | null;
  source_id: string | null;
  loss_reason_id: string | null;
  temperature: 'COLD' | 'WARM' | 'HOT' | 'CLOSED' | null;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  notes: string | null;
  stage_changed_at: string;
  owner_id: string | null;
  version: number;
  created_at: string;
}

export function getDeal(id: string): Promise<DealView> {
  return apiRequest(`/v1/crm/opportunities/${id}`);
}

export function updateDeal(id: string, body: Record<string, unknown>): Promise<DealView> {
  return apiRequest(`/v1/crm/opportunities/${id}`, { method: 'PATCH', body });
}

export function moveDealStage(
  id: string,
  body: { stageId: string; expectedVersion: number; lostReason?: string | null },
): Promise<DealView> {
  return apiRequest(`/v1/crm/opportunities/${id}/stage`, { method: 'POST', body });
}

// ---------------------------------------------------------------------------
// What else is attached to a record

export interface RelatedTask {
  id: string;
  title: string;
  status: string;
  due_date: string | null;
}

export function relatedTasks(
  entityType: CrmEntityType,
  id: string,
): Promise<{ tasks: RelatedTask[] }> {
  return apiRequest(`/v1/work/tasks?relatedType=${entityType}&relatedId=${id}&limit=20`);
}

export interface RelatedFile {
  id: string;
  name: string;
  sizeBytes: number;
  createdAt: string;
}

export function relatedFiles(
  entityType: CrmEntityType,
  id: string,
): Promise<{ files: RelatedFile[] }> {
  return apiRequest(`/v1/files?relatedEntityType=${entityType}&relatedEntityId=${id}&limit=20`);
}

/**
 * The activity kinds the timeline offers as tabs.
 *
 * Grouped rather than one tab per type: "e-mails" is a question somebody asks, "STATUS_CHANGED" is
 * not. `system` holds everything the platform did rather than a person, which is worth keeping and
 * not worth putting first.
 */
export const TIMELINE_TABS = {
  all: null,
  notes: ['NOTE'],
  emails: ['EMAIL'],
  calls: ['CALL', 'WHATSAPP', 'SMS'],
  meetings: ['MEETING'],
  tasks: ['TASK'],
  system: ['CREATED', 'STATUS_CHANGED', 'STAGE_CHANGED', 'OWNER_CHANGED', 'CONVERTED', 'SYSTEM'],
} as const;

export type TimelineTab = keyof typeof TIMELINE_TABS;

// ---------------------------------------------------------------------------
// The lead as a place somebody works

export const INTERACTIONS = ['NOTE', 'CALL', 'WHATSAPP', 'EMAIL', 'SMS', 'VISIT'] as const;

export type Interaction = (typeof INTERACTIONS)[number];

export const CALL_OUTCOMES = [
  'ANSWERED',
  'NO_ANSWER',
  'VOICEMAIL',
  'BUSY',
  'CALLBACK_REQUESTED',
  'WRONG_NUMBER',
] as const;

export type CallOutcome = (typeof CALL_OUTCOMES)[number];

export interface LeadWorkspace {
  completeness: { score: number; missing: { key: string; label: string }[]; totalWeight: number };
  interactions: number;
  unansweredCalls: number;
  lastInteractionAt: string | null;
  nextStep: { taskId: string; title: string; dueDate: string | null } | null;
}

export function getLeadWorkspace(id: string): Promise<LeadWorkspace> {
  return apiRequest(`/v1/crm/leads/${id}/workspace`);
}

export interface InteractionInput {
  type: Interaction;
  subject?: string | null;
  body: string;
  outcome?: CallOutcome;
  followUp?: { title: string; dueInDays?: number; priority?: string };
}

export function recordInteraction(
  id: string,
  input: InteractionInput,
): Promise<{ activity_id: string; task_id: string | null }> {
  return apiRequest(`/v1/crm/leads/${id}/interactions`, { method: 'POST', body: input });
}

export interface LeadMoment {
  id: string;
  name: string;
  code: string;
  color: string | null;
}

export function listLeadMoments(): Promise<{ moments: LeadMoment[] }> {
  return apiRequest('/v1/crm/leads/moments');
}

/**
 * A number somebody can dial or message, as the digits WhatsApp wants.
 *
 * Brazil-first: eight or nine digits plus a two-digit area code and no country code is a Brazilian
 * number somebody typed the way they say it out loud, so 55 goes on the front. Anything already
 * longer is left alone — it is either international or already complete, and guessing would break it.
 */
export function whatsappNumber(phone: string | null | undefined): string | null {
  if (!phone) return null;

  const digits = phone.replace(/\D/g, '');

  if (digits.length < 10) return null;

  return digits.length <= 11 ? `55${digits}` : digits;
}

// ---------------------------------------------------------------------------
// Tags and notes on a record (Phase 144)
// ---------------------------------------------------------------------------

export interface Tag {
  id: string;
  name: string;
  color: string | null;
}

export function listTags(): Promise<{ tags: Tag[] }> {
  return apiRequest('/v1/crm/tags');
}

/**
 * Tags a record, by name.
 *
 * The server creates the tag if the tenant has not used it before, which is what makes a tag a
 * word somebody types rather than a catalogue they have to maintain first.
 */
export function attachTag(
  entityType: CrmEntityType,
  id: string,
  name: string,
): Promise<{ id: string }> {
  return apiRequest(`/v1/crm/${entityType}/${id}/tags`, { method: 'POST', body: { name } });
}

export function detachTag(entityType: CrmEntityType, id: string, tagId: string): Promise<void> {
  return apiRequest(`/v1/crm/${entityType}/${id}/tags/${tagId}`, { method: 'DELETE' });
}

/**
 * A note on the record's timeline.
 *
 * Notes are activities, not a column: "o cliente pediu para ligar depois das 18h" belongs in the
 * history with a date and an author on it, not in a text box that the next person overwrites.
 */
export function addNote(entityType: CrmEntityType, id: string, body: string): Promise<Activity> {
  return apiRequest(`/v1/crm/${entityType}/${id}/activities`, {
    method: 'POST',
    body: { activityType: 'NOTE', body },
  });
}

// ---------------------------------------------------------------------------
// Qualifying somebody the CRM already knows (Phase 147)
// ---------------------------------------------------------------------------

export interface QualifyInput {
  title?: string | null;
  /** From the tenant's own catalogue (Configurações › Cadastros), never typed. */
  sourceId?: string | null;
  dealValueCents?: number | null;
}

/**
 * Raises a sales attempt on an existing contact.
 *
 * Not a conversion: the person stays one row and this is a second record *about* them, with its
 * own status and dates. That is what makes a qualification rate countable.
 */
export function qualifyContact(
  contactId: string,
  input: QualifyInput = {},
): Promise<{ id: string; name: string }> {
  return apiRequest(`/v1/crm/contacts/${contactId}/qualify`, { method: 'POST', body: input });
}
